import { useTranslation } from 'react-i18next';
import type { KeyboardEvent } from 'react';
import { cx } from '../lib/cx';
import styles from './TimeField.module.css';

export interface TimeFieldProps {
  /** Controlled text ("HH:mm" while untouched; free text while typing). */
  readonly value: string;
  readonly label: string;
  /** The committed text does not parse as a time (2026-10b §3.8 Invalid). */
  readonly invalid?: boolean;
  /** The time lies past 24:00 of the personal day: an 11px "next day" suffix. */
  readonly nextDay?: boolean;
  readonly disabled?: boolean;
  /** Shown while the field is empty (an optional time such as a check-in plan, "--:--"). */
  readonly placeholder?: string;
  /** Tooltip, e.g. the merged-group "drag to adjust" hint. */
  readonly title?: string;
  readonly testId?: string;
  /** Extra class on the outer wrapper (layout only; the input keeps its own styling). */
  readonly className?: string;
  readonly onChange: (text: string) => void;
  /** Enter or blur: the parent re-validates the format (§3.8 Commit). */
  readonly onCommit: () => void;
  /** Esc: returns true when it was consumed (the field was modified and got restored, §5.4). */
  readonly onEscape: () => boolean;
  /** Arrow keys: move to the next step grid line in the direction; `byHour` = Shift (±60min). */
  readonly onStep?: (direction: 1 | -1, byHour: boolean) => void;
  /** Enter after the commit: move focus to the next field. */
  readonly onEnterNext?: () => void;
}

/**
 * 24-hour time text input (2026-10b §3.8): 64px wide, monospaced digits, format checked only
 * on commit (Enter or blur). Esc restores the baseline value and keeps focus; the arrow keys
 * walk the step grid. Shared by the picker's start/end fields (and later by templates and
 * check-in planned times).
 */
export function TimeField(props: TimeFieldProps) {
  const { t } = useTranslation();

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Escape') {
      if (props.onEscape()) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      props.onCommit();
      props.onEnterNext?.();
      return;
    }
    if ((event.key === 'ArrowUp' || event.key === 'ArrowDown') && props.onStep !== undefined) {
      event.preventDefault();
      // The timeline direction: up is earlier (2026-10b §3.5); Shift moves by a whole hour.
      props.onStep(event.key === 'ArrowUp' ? -1 : 1, event.shiftKey);
    }
  };

  return (
    <span className={cx(styles.wrap, props.className)}>
      <input
        className={cx(styles.input, props.invalid === true && styles.invalid)}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={props.value}
        placeholder={props.placeholder}
        aria-label={props.label}
        aria-invalid={props.invalid === true ? true : undefined}
        disabled={props.disabled}
        title={props.title}
        data-testid={props.testId}
        onChange={(event) => props.onChange(event.target.value)}
        onFocus={(event) => event.target.select()}
        onBlur={() => props.onCommit()}
        onKeyDown={onKeyDown}
      />
      {props.nextDay === true ? (
        <span
          className={styles.nextDay}
          data-testid={props.testId === undefined ? undefined : `${props.testId}-next-day`}
        >
          {t('picker.nextDay')}
        </span>
      ) : null}
    </span>
  );
}
